import { Check, ExternalLink, Route, ShieldCheck, Sparkles } from "lucide-react";
import { course } from "../content/course";
import type { AssessmentAttempt } from "../lib/progress";

type CompletionPageProps = {
  passedAt: string;
  attempts: AssessmentAttempt[];
  onExploreRoutes: () => void;
};

export function CompletionPage({
  passedAt,
  attempts,
  onExploreRoutes,
}: CompletionPageProps) {
  const latestScore = attempts.at(-1)?.score ?? course.passPercentage;

  return (
    <main id="main-content" className="completion-page">
      <section className="completion-hero">
        <div className="completion-glow" />
        <span className="completion-icon">
          <ShieldCheck size={34} />
        </span>
        <p className="eyebrow">Course complete</p>
        <h1>You kept the human in the loop.</h1>
        <p>
          You finished the shared AI Day preparation path and demonstrated the
          boundaries for safe, verifiable AI work.
        </p>
        <div className="completion-stats">
          <div>
            <span>Final score</span>
            <strong>{latestScore}%</strong>
          </div>
          <div>
            <span>Path</span>
            <strong>VW Delivery</strong>
          </div>
          <div>
            <span>Completed</span>
            <strong>
              {new Intl.DateTimeFormat("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              }).format(new Date(passedAt))}
            </strong>
          </div>
        </div>
      </section>

      <section className="completion-grid">
        <article>
          <span className="route-icon">
            <Sparkles size={22} />
          </span>
          <h2>Your operating principles</h2>
          <ul className="completion-list">
            <li>
              <Check size={17} /> Treat output as a candidate, not evidence.
            </li>
            <li>
              <Check size={17} /> Use approved tools and protect the data boundary.
            </li>
            <li>
              <Check size={17} /> Separate inspection, action, and verification.
            </li>
            <li>
              <Check size={17} /> Increase autonomy only after measured reliability.
            </li>
          </ul>
        </article>
        <article>
          <span className="route-icon">
            <Route size={22} />
          </span>
          <h2>Continue the journey</h2>
          <p>
            The AI Day breakouts go deeper into product use, everyday Codex
            workflows, and advanced agent building.
          </p>
          <button className="secondary-button" type="button" onClick={onExploreRoutes}>
            Return to course overview
          </button>
        </article>
      </section>

      <section className="source-panel">
        <div>
          <p className="eyebrow">Keep the evidence close</p>
          <h2>Course sources and WirelessCar guidance</h2>
          <p>
            These references support the course material. Current
            delivery-specific WirelessCar guidance remains authoritative for
            real work.
          </p>
        </div>
        <ul>
          {course.sources.map((source) => (
            <li key={source.id}>
              <a href={source.url} target="_blank" rel="noreferrer">
                {source.title} <ExternalLink size={15} />
              </a>
              <small>Reviewed {source.reviewedAt}</small>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
